/**
 * Opportunities UI environment. The same screens serve four places, each wrapping them thinly in a provider:
 *   customer  /opportunities            a customer workspace sending requirements to Quixo or to its agency
 *   purchase  /opportunities/purchases  an agency buying from Quixo (partner portal)
 *   quixo     /opportunities            Quixo's sales desk (admin console)
 *   agency    /opportunities            an agency's own sales desk (partner portal)
 * A surface supplies its translator, locale, roles, transport, link paths and page frame; the screens never import a surface.
 */
import { createContext, useContext, type ComponentType, type ReactNode } from 'react';
import type { Locale, Translate } from '@qm/i18n';

export type ApiPath = `/v1/${string}`;
export interface OppHttp {
  get<T>(path: ApiPath): Promise<T>;
  send<T>(method: 'POST' | 'PUT', path: ApiPath, body?: unknown): Promise<T>;
}
/** Status code of a failed call (0 when unknown), and the API's reason code if it sent one. */
export interface OppFailure { status: number; reason?: string; message?: string }

export interface ScreenProps { title: string; subtitle?: string; eyebrow?: string; actions?: ReactNode; testId?: string; children: ReactNode }

export type BuyerVariant = 'customer' | 'purchase';
export type DeskVariant = 'quixo' | 'agency';

export interface OppEnv {
  variant: BuyerVariant | DeskVariant;
  t: Translate;
  locale: Locale;
  roles: readonly string[];
  http: OppHttp;
  /** turns a thrown transport error into status / reason */
  failure: (e: unknown) => OppFailure;
  /** API base: the buyer routes (`/v1/opportunities`, `/v1/opportunities/agency/purchases`) or the seller desk (`.../admin`, `.../agency`) */
  apiBase: ApiPath;
  /** where the screens link to */
  links: { list: string; detail: (id: string) => string; staff: string };
  Screen: ComponentType<ScreenProps>;
  /** desk only: which roles may sell, assign the account manager, manage staff, and act for the account manager (the API enforces the same) */
  can: { sell: (roles: readonly string[]) => boolean; assign: (roles: readonly string[]) => boolean; manage: (roles: readonly string[]) => boolean; actAs: (roles: readonly string[]) => boolean };
}

const Ctx = createContext<OppEnv | null>(null);
export function OpportunitiesProvider({ env, children }: { env: OppEnv; children: ReactNode }) { return <Ctx.Provider value={env}>{children}</Ctx.Provider>; }
export function useOpp(): OppEnv {
  const v = useContext(Ctx);
  if (!v) throw new Error('useOpp outside OpportunitiesProvider');
  return v;
}

/** Wording that differs per variant: `opportunities.<scope>.<name>`. */
export function useCopy() {
  const { t, variant } = useOpp();
  const scope = variant === 'purchase' ? 'purchases' : variant === 'agency' ? 'agency' : variant === 'quixo' ? 'admin' : '';
  return {
    t,
    /** title / intro / empty / new for buyer screens; title / intro / staffLink for desk screens */
    text: (name: string) => t(scope ? `opportunities.${scope}.${name}` : `opportunities.${name}`),
    staff: (name: string) => t(`opportunities.${scope || 'admin'}.staff.${name}`),
  };
}
